import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
  Loader2,
  LogOut,
  CalendarDays,
  Phone,
  MapPin,
  X,
  Sparkles,
  Pencil,
  CheckCircle2,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  getCustomerToken,
  loginCustomer,
  fetchMe,
  fetchMyAppointments,
  cancelMyAppointment,
  rescheduleMyAppointment,
  logoutCustomer,
} from "@/lib/customerAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatLocal(dt) {
  return dt.toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatTime12(hhmm) {
  const [h, m] = hhmm.split(":");
  let hi = parseInt(h, 10);
  const ampm = hi >= 12 ? "PM" : "AM";
  hi = hi % 12 || 12;
  return `${hi}:${m} ${ampm}`;
}

function formatDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function LoginForm({ onLoggedIn, initialEmail }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await loginCustomer({ email: email.trim(), password });
      toast.success("Welcome back!");
      onLoggedIn();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FDFBF7] px-4">
      <form
        onSubmit={submit}
        className="comic-card-static p-7 max-w-md w-full"
        data-testid="customer-login-form"
      >
        <div className="flex items-center gap-3 mb-5">
          <span className="inline-block w-10 h-10 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] grid place-items-center">
            <Sparkles className="w-5 h-5 text-[#0B132B]" />
          </span>
          <div>
            <div className="font-['Bebas_Neue'] text-3xl tracking-wide leading-none">
              Customer Login
            </div>
            <div className="text-sm text-[#0B132B]/70">Daniel's Dirty Deeds</div>
          </div>
        </div>
        <label className="block text-sm font-semibold mb-1">Email</label>
        <input
          data-testid="customer-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="comic-input"
          required
          autoFocus={!initialEmail}
        />
        <label className="block text-sm font-semibold mt-3 mb-1">Password</label>
        <div className="relative">
          <input
            data-testid="customer-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="comic-input"
            style={{ paddingRight: "2.75rem" }}
            required
            autoFocus={!!initialEmail}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            data-testid="customer-password-toggle"
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[#FFF4C2] text-[#0B132B]"
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="mt-5 w-full comic-btn comic-btn-primary"
          data-testid="customer-login-submit"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Log In
        </button>
        <a href="/" className="block text-center mt-4 text-sm text-[#0B132B]/70 underline">
          ← Back to home
        </a>
        <p className="text-center text-xs text-[#0B132B]/60 mt-4">
          Don't have an account yet? Book your first cleaning on the home page — you'll set a password during checkout.
        </p>
      </form>
    </div>
  );
}

function RescheduleModal({ appt, onClose, onDone }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!date) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    const dStr = formatDateLocal(date);
    axios
      .get(`${API}/availability`, { params: { date_str: dStr, num_bins: appt.num_bins || 1 } })
      .then((res) => setSlots(res.data?.slots || []))
      .catch(() => toast.error("Couldn't load times"))
      .finally(() => setLoadingSlots(false));
  }, [date, appt.num_bins]);

  const save = async () => {
    if (!date || !selectedTime) return;
    setSubmitting(true);
    try {
      const [hh, mm] = selectedTime.split(":").map((v) => parseInt(v, 10));
      const local = new Date(date);
      local.setHours(hh, mm, 0, 0);
      const iso = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(
        2, "0"
      )}-${String(local.getDate()).padStart(2, "0")}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
      await rescheduleMyAppointment(appt.id, iso);
      toast.success("Rescheduled");
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B132B]/60 grid place-items-center px-4">
      <div className="comic-card-static p-5 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="font-['Bebas_Neue'] text-2xl">Reschedule</div>
          <button onClick={onClose} className="text-[#0B132B]/60">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#0B132B]/70 mb-3">
          Currently scheduled: <strong>{formatLocal(new Date(appt.start_at))}</strong>
        </p>
        <div className="flex justify-center">
          <div className="bg-white p-2 rounded-xl border-[3px] border-[#0B132B] shadow-[4px_4px_0_#0B132B]">
            <DayPicker mode="single" selected={date} onSelect={setDate} weekStartsOn={0} />
          </div>
        </div>
        {date && loadingSlots && (
          <div className="flex items-center gap-2 mt-3 text-[#0B132B]/80">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading times…
          </div>
        )}
        {date && !loadingSlots && slots.length > 0 && (
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {slots.map((s) => (
              <button
                type="button"
                key={s.time}
                disabled={!s.available}
                onClick={() => setSelectedTime(s.time)}
                data-selected={selectedTime === s.time ? "true" : "false"}
                className="slot"
              >
                {formatTime12(s.time)}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="comic-btn comic-btn-outline">Cancel</button>
          <button
            onClick={save}
            disabled={!date || !selectedTime || submitting}
            className="comic-btn comic-btn-primary"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save new time
          </button>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [me, setMe] = useState(null);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reschedAppt, setReschedAppt] = useState(null);
  const [searchParams] = useSearchParams();

  const reload = async () => {
    setLoading(true);
    try {
      const [m, a] = await Promise.all([fetchMe(), fetchMyAppointments()]);
      setMe(m);
      setAppts(a || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        logoutCustomer();
        onLogout();
      } else toast.error("Couldn't load account");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open reschedule from email-link query (?appt=ID)
  useEffect(() => {
    const targetId = searchParams.get("appt");
    if (targetId && appts.length > 0) {
      const a = appts.find((x) => x.id === targetId && x.status !== "cancelled");
      if (a) setReschedAppt(a);
    }
  }, [appts, searchParams]);

  const upcoming = appts.filter(
    (a) => new Date(a.start_at) >= new Date(Date.now() - 1000) && a.status !== "cancelled"
  );
  const past = appts.filter(
    (a) => new Date(a.start_at) < new Date(Date.now() - 1000) || a.status === "cancelled"
  );

  const handleCancel = async (a) => {
    if (!window.confirm("Cancel this appointment?")) return;
    try {
      await cancelMyAppointment(a.id);
      toast.success("Appointment cancelled");
      reload();
    } catch {
      toast.error("Couldn't cancel");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0B132B] text-white border-b-4 border-[#0B132B]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-block w-9 h-9 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] grid place-items-center">
              <Sparkles className="w-4 h-4 text-[#0B132B]" />
            </span>
            <div className="font-['Bebas_Neue'] text-2xl sm:text-3xl tracking-wider">
              <span className="text-[#00E676]">MY</span> ACCOUNT
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="hidden sm:inline-flex comic-btn comic-btn-outline text-sm"
               style={{ padding: "0.45rem 0.8rem", color: "#0B132B" }}>
              Home
            </a>
            <button
              data-testid="customer-logout"
              onClick={() => {
                logoutCustomer();
                onLogout();
              }}
              className="comic-btn comic-btn-action"
              style={{ padding: "0.5rem 1rem", fontSize: "0.95rem" }}
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {loading && (
          <div className="flex items-center gap-2 text-[#0B132B]/80">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && me && (
          <div className="mb-6">
            <div className="font-['Bebas_Neue'] text-3xl">Hi, {me.name?.split(" ")[0]}!</div>
            <div className="text-sm text-[#0B132B]/70">{me.email}</div>
          </div>
        )}

        <div className="mb-3 font-['Bebas_Neue'] text-xl">Upcoming ({upcoming.length})</div>
        {!loading && upcoming.length === 0 && (
          <div className="comic-card-static p-6 text-[#0B132B]/70 mb-6">
            No upcoming appointments — <a href="/" className="underline font-semibold">book one</a>.
          </div>
        )}
        <div className="space-y-3 mb-8">
          {upcoming.map((a) => (
            <div key={a.id} className="comic-card-static p-4" data-testid={`my-appt-${a.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-['Bebas_Neue'] text-2xl">
                    {formatLocal(new Date(a.start_at))}
                  </div>
                  <div className="text-sm text-[#0B132B]/80 mt-1 flex flex-wrap gap-x-3">
                    <span>{a.num_bins} bin{a.num_bins > 1 ? "s" : ""}</span>
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> {a.address}, {a.city}
                    </span>
                    <span>${Number(a.total_amount).toFixed(2)}</span>
                  </div>
                  {a.customer_confirmed && (
                    <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#00A76F]">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Confirmed
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setReschedAppt(a)}
                    className="comic-btn comic-btn-blue"
                    style={{ padding: "0.5rem 0.9rem", fontSize: "0.9rem" }}
                    data-testid={`resched-btn-${a.id}`}
                  >
                    <Pencil className="w-4 h-4" /> Reschedule
                  </button>
                  <button
                    onClick={() => handleCancel(a)}
                    className="comic-btn comic-btn-action"
                    style={{ padding: "0.5rem 0.9rem", fontSize: "0.9rem" }}
                    data-testid={`cancel-btn-${a.id}`}
                  >
                    <X className="w-4 h-4" /> Cancel
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {past.length > 0 && (
          <>
            <div className="mb-3 font-['Bebas_Neue'] text-xl">Past & Cancelled</div>
            <div className="space-y-2">
              {past.map((a) => (
                <div key={a.id} className="comic-card-static p-3 opacity-70">
                  <div className="text-sm">
                    <span className="font-semibold">{formatLocal(new Date(a.start_at))}</span>
                    {" — "}
                    <span>{a.num_bins} bin{a.num_bins > 1 ? "s" : ""}</span>
                    {a.status === "cancelled" && (
                      <span className="text-[#FF3D00] font-semibold ml-2">CANCELLED</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {reschedAppt && (
        <RescheduleModal
          appt={reschedAppt}
          onClose={() => setReschedAppt(null)}
          onDone={() => {
            setReschedAppt(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

export default function Account() {
  const [authed, setAuthed] = useState(null);
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";

  useEffect(() => {
    document.title = "My Account · Daniel's Dirty Deeds";
    if (!getCustomerToken()) {
      setAuthed(false);
      return;
    }
    fetchMe()
      .then(() => setAuthed(true))
      .catch(() => {
        logoutCustomer();
        setAuthed(false);
      });
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FDFBF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E88E5]" />
      </div>
    );
  }
  if (!authed) return <LoginForm initialEmail={initialEmail} onLoggedIn={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
}
